import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Footer from '../components/layout/Footer';
import authService from '../services/authService';

const POLL_INTERVAL_MS = 3000;
const PENDING_EMAIL_KEY = 'pendingVerificationEmail';
const postVerificationLoginState = { from: '/profile' };

const resolvePendingEmail = (location) => {
    const params = new URLSearchParams(location.search);
    const fromQuery = params.get('email')?.trim() || '';
    const fromState = location.state?.email?.trim() || '';
    const fromSession = sessionStorage.getItem(PENDING_EMAIL_KEY)?.trim() || '';
    return fromQuery || fromState || fromSession || '';
};

const VerifyEmailPending = () => {
    const { t } = useTranslation('auth');
    const location = useLocation();
    const navigate = useNavigate();
    const [email, setEmail] = useState(() => resolvePendingEmail(location));
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [verified, setVerified] = useState(false);
    const pollFailures = useRef(0);

    useEffect(() => {
        const resolved = resolvePendingEmail(location);
        if (resolved && resolved !== email) {
            setEmail(resolved);
        }
    }, [location.search, location.state, email]);

    useEffect(() => {
        if (email) {
            sessionStorage.setItem(PENDING_EMAIL_KEY, email);
        }
    }, [email]);

    const checkStatus = useCallback(async () => {
        if (!email || verified) return;

        try {
            const result = await authService.getVerificationStatus(email);
            pollFailures.current = 0;
            if (result.verified) {
                setVerified(true);
                setError('');
                sessionStorage.removeItem(PENDING_EMAIL_KEY);
            }
        } catch {
            pollFailures.current += 1;
            if (pollFailures.current >= 3) {
                setError(t('verifyEmailPending.pollingError'));
            }
        }
    }, [email, verified, t]);

    useEffect(() => {
        if (!email || verified) return;

        checkStatus();

        const intervalId = setInterval(checkStatus, POLL_INTERVAL_MS);

        const handleVisibility = () => {
            if (document.visibilityState === 'visible') {
                checkStatus();
            }
        };

        const handleFocus = () => {
            checkStatus();
        };

        document.addEventListener('visibilitychange', handleVisibility);
        window.addEventListener('focus', handleFocus);

        return () => {
            clearInterval(intervalId);
            document.removeEventListener('visibilitychange', handleVisibility);
            window.removeEventListener('focus', handleFocus);
        };
    }, [email, verified, checkStatus]);

    const handleResend = async (e) => {
        e.preventDefault();

        if (!email) {
            setError(t('verifyEmailPending.emailRequired'));
            return;
        }

        setLoading(true);
        setError('');
        setMessage('');

        try {
            await authService.resendVerification(email);
            setMessage(t('verifyEmailPending.resendSuccess'));
            pollFailures.current = 0;
        } catch (err) {
            setError(err.message || t('verifyEmailPending.resendFailed'));
        } finally {
            setLoading(false);
        }
    };

    if (verified) {
        return (
            <div className="bg-white min-h-screen font-montserrat">
                <section className="relative w-full h-[200px] sm:h-[250px] md:h-[300px] flex items-center justify-center text-white text-center overflow-hidden mb-0 px-4 sm:px-6 lg:px-8" style={{ margin: '10px auto' }}>
                    <div className="absolute inset-0 bg-gradient-to-r from-green-600 to-green-800 z-0" />
                    <div className="relative z-20 flex flex-col items-center justify-center w-full h-full">
                        <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-normal mb-3 sm:mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>{t('verifyEmailPending.verifiedTitle')}</h1>
                        <p className="text-base sm:text-lg md:text-xl max-w-2xl mx-auto opacity-90 px-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>{t('verifyEmailPending.verifiedSubtitle')}</p>
                    </div>
                </section>

                <div className="w-full border-t border-gray-200 my-0" />

                <section className="py-8 sm:py-12 lg:py-16 max-w-md mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <div className="mb-6 p-4 bg-green-50 border border-green-200 text-green-700 rounded">
                        {t('verifyEmailPending.verifiedMessage')}
                    </div>
                    <button
                        type="button"
                        onClick={() => navigate('/login', { replace: true, state: postVerificationLoginState })}
                        className="w-full bg-black text-white px-6 sm:px-8 py-3 sm:py-4 font-normal border-0 hover:bg-gray-900 transition-colors text-sm sm:text-base"
                        style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 400 }}
                    >
                        {t('verifyEmailPending.goToLogin')}
                    </button>
                </section>

                <Footer />
            </div>
        );
    }

    return (
        <div className="bg-white min-h-screen font-montserrat">
            <section className="relative w-full h-[200px] sm:h-[250px] md:h-[300px] flex items-center justify-center text-white text-center overflow-hidden mb-0 px-4 sm:px-6 lg:px-8" style={{ margin: '10px auto' }}>
                <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-blue-800 z-0" />
                <div className="relative z-20 flex flex-col items-center justify-center w-full h-full">
                    <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-normal mb-3 sm:mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>{t('verifyEmailPending.heroTitle')}</h1>
                    <p className="text-base sm:text-lg md:text-xl max-w-2xl mx-auto opacity-90 px-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>{t('verifyEmailPending.heroSubtitle')}</p>
                </div>
            </section>

            <div className="w-full border-t border-gray-200 my-0" />

            <section className="py-8 sm:py-12 lg:py-16 max-w-md mx-auto px-4 sm:px-6 lg:px-8">
                <p className="text-gray-700 mb-6 text-center" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                    {t('verifyEmailPending.message')}
                </p>

                <p className="text-gray-500 mb-6 text-center text-sm" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                    {t('verifyEmailPending.pollingNote')}
                </p>

                {error && (
                    <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded">
                        {error}
                    </div>
                )}

                {message && (
                    <div className="mb-6 p-4 bg-green-50 border border-green-200 text-green-700 rounded">
                        {message}
                    </div>
                )}

                <form onSubmit={handleResend} className="space-y-4 sm:space-y-6">
                    <div>
                        <label htmlFor="email" className="block text-sm uppercase tracking-widest mb-2" style={{ color: '#979797', fontSize: '13px', fontFamily: 'Montserrat, sans-serif' }}>
                            {t('verifyEmailPending.email')}
                        </label>
                        <input
                            id="email"
                            name="email"
                            type="email"
                            autoComplete="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            disabled={loading}
                            className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-200 focus:outline-none focus:border-black transition-colors disabled:bg-gray-50 disabled:cursor-not-allowed text-sm sm:text-base"
                            style={{ fontFamily: 'Montserrat, sans-serif' }}
                            placeholder={t('verifyEmailPending.emailPlaceholder')}
                        />
                    </div>

                    <div>
                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full bg-black text-white px-6 sm:px-8 py-3 sm:py-4 font-normal border-0 hover:bg-gray-900 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed relative text-sm sm:text-base"
                            style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 400 }}
                        >
                            {loading ? (
                                <>
                                    <span className="opacity-0">{t('verifyEmailPending.resend')}</span>
                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    </div>
                                </>
                            ) : (
                                t('verifyEmailPending.resend')
                            )}
                        </button>
                    </div>
                </form>

                <div className="mt-8 text-center">
                    <Link to="/login" className="text-black hover:underline" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                        {t('verifyEmailPending.backToLogin')}
                    </Link>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default VerifyEmailPending;
